import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Tabs from "@shared/Tabs";
import AllProjects from "./all-projects";
import { getAllDeveloperProjects } from "@services/krentDeveloper";
import {
  DevProject,
  DeveloperProjectsResponse,
} from "@interfaces/app.interface";
import { DeveloperDashboardLayout } from "@layouts/DeveloperDashboardLayout";
import { useSessionStorage } from "../../../hooks";
import DeveloperSearchInput from "components/developerSearch";

const ITEMS_PER_PAGE = 10;

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<DevProject[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<DevProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTimeline, setCurrentTimeline] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [user] = useSessionStorage("user", {});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchProjects = useCallback(
    async (timeline: string, page: number, limit: number) => {
      setLoading(true);
      setError(null);

      try {
        const response: DeveloperProjectsResponse =
          await getAllDeveloperProjects(user.id, timeline, page, limit);

        const projectsData = response.data as DevProject[];
        setProjects(projectsData);
        setFilteredProjects(projectsData);

        if (response.pagination?.totalPages) {
          setTotalPages(response.pagination.totalPages);
        }
      } catch (error) {
        setError("Failed to fetch projects. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [user.id]
  );

  useEffect(() => {
    fetchProjects(currentTimeline, currentPage, ITEMS_PER_PAGE);
  }, [fetchProjects, currentTimeline, currentPage]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredProjects(projects);
      return;
    }

    const filtered = projects.filter((project) =>
      project.projectName.toLowerCase().includes(term.toLowerCase().trim())
    );
    setFilteredProjects(filtered);
  };

  const handleTabChange = (tabName: string) => {
    let timeline: string;
    switch (tabName) {
      case "Current Projects":
        timeline = "current";
        break;
      case "Previous Projects":
        timeline = "previous";
        break;
      case "Future Projects":
        timeline = "future";
        break;
      default:
        timeline = "all";
    }
    setCurrentTimeline(timeline);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const components = {
    "All Projects": (
      <AllProjects
        projects={filteredProjects}
        // client={user}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    ),
    "Current Projects": (
      <AllProjects
        projects={filteredProjects}
        // client={user}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    ),
    "Previous Projects": (
      <AllProjects
        projects={filteredProjects}
        // client
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    ),
    "Future Projects": (
      <AllProjects
        projects={filteredProjects}
        // client={user}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    ),
  };

  const handleAddDetails = () => {
    navigate("/developer/project-form");
  };

  return (
    <DeveloperDashboardLayout title="My Projects">
      <div>
        <div className="flex items-center gap-2">
          <p>Home</p>
          <i className="fa-light fa-angle-right"></i>
          <p className="">Dashboard</p>
          <i className="fa-light fa-angle-right"></i>
          <p className="text-primary">Projects</p>
        </div>
        <div>
          <div className="my-10">
            <DeveloperSearchInput
              placeholder="Search Projects"
              onSearch={handleSearch}
              initialValue={searchTerm}
            />
          </div>
          <div>
            <div className="float-right mb-4">
              <button
                className="fw-bold text-white bg-primary rounded-10 p-2 px-3"
                onClick={handleAddDetails}
              >
                + Add a new project
              </button>
            </div>
            <div className="my-10">
              <Tabs
                components={components}
                onTabChange={handleTabChange}
                loading={loading}
                error={error}
              />
            </div>
          </div>
        </div>
      </div>
    </DeveloperDashboardLayout>
  );
};

export default Projects;
